import React, { useRef } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  Spinner,
  Divider,
  useDisclosure,
  Portal,
} from "@chakra-ui/react";
import { Bell } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../contexts/NotificationContext";
import { tt } from "../i18n/tt";
import { st } from "../i18n/serverCopy";
import { fmtRelative } from "../lib/formatLocale";
import { categoryMeta } from "../lib/notificationCategories";

export default function NotificationPopover() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    unreadCount,
    notifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotification();
  const [loading, setLoading] = React.useState(false);
  const initialFocusRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleOpen = async () => {
    onOpen();
    setLoading(true);
    await fetchNotifications();
    setLoading(false);
  };

  const handleItemClick = async (notif) => {
    if (!notif.is_read) {
      await markAsRead(notif.id);
    }
    const isVideoRights = location.pathname.startsWith("/video/");
    const basePath = isVideoRights ? "/video/notice" : "/notice";
    onClose();
    navigate(`${basePath}?tab=notifications&focus=${notif.id}`);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const formatTime = (dateStr) => {
    try {
      return fmtRelative(dateStr);
    } catch {
      return "";
    }
  };

  return (
    <Popover
      placement="bottom-end"
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
      isLazy
    >
      <PopoverTrigger>
        <Box position="relative" display="inline-flex" cursor="pointer">
          <IconButton
            icon={<Bell size={18} />}
            variant="ghost"
            size="sm"
            aria-label={tt("a.d3f1b4c4", "알림")}
            color="neutral.500"
            _hover={{ color: "brand.600", bg: "neutral.100" }}
            ref={initialFocusRef}
          />
          {unreadCount > 0 && (
            <Box
              position="absolute"
              top="-2px"
              right="-2px"
              bg="red.500"
              color="white"
              fontSize="xs"
              fontWeight="bold"
              borderRadius="full"
              minW="18px"
              h="18px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={1}
              lineHeight="1"
              pointerEvents="none"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Box>
          )}
        </Box>
      </PopoverTrigger>
      {/* 2026-09-27: Portal — 상단바(sticky, z 1000) 안에 그리면 팝오버 z 1500 이 헤더의 stacking context 에 갇혀 레이 위젯(1200)·GuideBar(1300) 아래로 깔린다 */}
      <Portal>
      <PopoverContent
        w={{ base: "320px", md: "380px" }}
        maxH="480px"
        boxShadow="lg"
        borderColor="neutral.200"
      >
        <PopoverArrow />
        <PopoverHeader
          borderBottomWidth="1px"
          borderColor="neutral.100"
          py={3}
          px={4}
        >
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold" fontSize="sm">{tt("a.d3f1b4c4", "알림")}</Text>
            {unreadCount > 0 && (
              <Button
                size="xs"
                variant="ghost"
                color="brand.500"
                onClick={handleMarkAllRead}
                _hover={{ bg: "brand.50" }}
              >
                {tt("a.d67ead37", "모두 읽음")}
              </Button>
            )}
          </Flex>
        </PopoverHeader>
        <PopoverBody p={0} overflowY="auto" maxH="400px">
          {loading ? (
            <Flex justify="center" align="center" minH="100px">
              <Spinner size="sm" />
            </Flex>
          ) : notifications.length === 0 ? (
            <Flex
              justify="center"
              align="center"
              minH="100px"
              direction="column"
              gap={2}
            >
              <Bell size={24} color="#A0A0A0" />
              <Text color="neutral.400" fontSize="sm">
                {tt("a.deeca28b", "새로운 알림이 없습니다.")}
              </Text>
            </Flex>
          ) : (
            notifications.map((notif, idx) => {
              const meta = categoryMeta(notif.category);
              const IconComp = meta.icon;
              const iconColor = meta.color;
              return (
                <React.Fragment key={notif.id}>
                  <Flex
                    px={4}
                    py={3}
                    gap={3}
                    align="flex-start"
                    cursor="pointer"
                    bg={notif.is_read ? "white" : "brand.50"}
                    _hover={{ bg: notif.is_read ? "neutral.50" : "brand.100" }}
                    transition="background 0.15s"
                    onClick={() => handleItemClick(notif)}
                  >
                    <Box
                      flexShrink={0}
                      mt="2px"
                      color={iconColor}
                    >
                      <IconComp size={16} />
                    </Box>
                    <Box flex={1} minW={0}>
                      <Text
                        fontSize="sm"
                        fontWeight={notif.is_read ? "normal" : "600"}
                        color="neutral.800"
                        noOfLines={1}
                      >
                        {st(notif.title)}
                      </Text>
                      {notif.body && (
                        <Text
                          fontSize="xs"
                          color="neutral.500"
                          noOfLines={2}
                          mt={0.5}
                        >
                          {st(notif.body)}
                        </Text>
                      )}
                      <Text fontSize="xs" color="neutral.400" mt={1}>
                        {formatTime(notif.created_at)}
                      </Text>
                    </Box>
                    {!notif.is_read && (
                      <Box
                        flexShrink={0}
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="brand.500"
                        mt="6px"
                      />
                    )}
                  </Flex>
                  {idx < notifications.length - 1 && (
                    <Divider borderColor="neutral.100" />
                  )}
                </React.Fragment>
              );
            })
          )}
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  );
}
